/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, PHASE_INFO, PHASE_INFO_FOR_ROUTINE, PHASE_INFO_FOR_MONITOR, ASYNC_ERROR_INFO } from './constants';
import error from '../../../../common/nodejs/errors';

export default {
  name: 'requirement161',
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      isDispose: true,
      systemId: '',
      modal_requirement_161: false,
      exitPromise: null
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    // ダイアログのヘッダー色
    headerColor() { return PHASE_INFO[this.phase]?.['headerColor']; },

    // ダイアログヘッダ―文言
    headerTitle() {
      const isValid = this.$te(PHASE_INFO[this.phase]?.['headerTitle']);
      return isValid ? this.$t(PHASE_INFO[this.phase]['headerTitle']) : '';
    },

    // ダイアログボディ文言
    workSupportMessage() { return this.$t(PHASE_INFO[this.phase]?.['workSupportMessage'](this.i18nWsKey)); },

    /** OKボタンの表示有無 */
    showButtonOk() { return PHASE_INFO[this.phase]?.showButtonOk; },

    /** キャンセルボタンの表示有無 */
    showButtonCancel() {
      return this.phase === PHASE.WAIT;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_161 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 待機処理 */
    async read() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data.user_data.is_dispose;
        // 正常コールバック 
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 実行(ブリード修正(1回目))へ遷移
            this.phase = PHASE.FIRST_EXECUTION;
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_161_EXECUTION_CONDITIONS_FAILED:
                // 実行条件不成立表示へ遷移
                await this.errorProcess(PHASE.EXECUTION_ERROR);
                break;
              case error.WSP_161_CANNOT_START_FAILED:
                // 開始不可表示へ遷移
                await this.errorProcess(PHASE.START_ERROR);
                break;
              case error.WSP_GENERAL_ERROR:
              default:
                // 共通エラーへ遷移
                await this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 共通エラーへ遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 */
    async execute() {
      try {
        this.isLoading = true;

        const apiType = PHASE_INFO_FOR_ROUTINE[this.phase]['apiType'];
        this.phase = PHASE_INFO_FOR_ROUTINE[this.phase]['monitorPhase'];

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: `execute-${apiType}`
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            switch (this.phase) {
              case PHASE.AIR_RUN_MONITORING:
                // 現在のフェーズが実行監視(エア混入確認)：再度実行処理(TMC Test)を実行
                await this.execute();
                break;
              case PHASE.PIPE_RUN_MONITORING:
                // 現在のフェーズが実行監視(パイプ接続確認)：停止処理を実行
                await this.stopProcess();
                break;
              default:
                // 上記以外：次の実行画面へ遷移
                this.phase = PHASE_INFO_FOR_MONITOR[this.phase]['nextPhase'];
                break;
            }
            break;
          // 異常終了
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_161_AIR_BLEEDING_FAILED:
              case error.WSP_161_BREAK_ABNORMAL_FAILED:
              case error.WSP_161_PIPE_ABNORMAL_FAILED:
              case error.WSP_161_AIR_ENTERS_FAILED:
                // 各種エラー表示へ遷移
                await this.errorProcess(ASYNC_ERROR_INFO[response.data.async_code]['nextPhase']);
                break;
              case error.WSP_GENERAL_ERROR:
              default:
                // 共通エラーへ遷移
                await this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 共通エラーへ遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** terminate処理 */
    async terminate() {
      try {
        this.isLoading = true;

        //  バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate',
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** Dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose',
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, "dispose"); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, "dispose"); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /** 終了/dispose処理 */
    async exitProcess() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
    },

    /** 停止遷移時の処理 */
    async stopProcess() {
      await this.exitProcess();
      this.phase = PHASE.SUCCESS;
    },

    /** エラー時の処理
     * @param {String} phase フェーズ名
     */
    async errorProcess(phase) {
      await this.exitProcess();
      this.phase = phase;
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      await this.exitProcess();
      this.phase = PHASE.FAILED;
    },

    /** OKボタン押下時の処理 */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.WAIT:
          this.read();
          break;
        case PHASE.FIRST_EXECUTION:
        case PHASE.SECOND_EXECUTION:
        case PHASE.THIRD_EXECUTION:
        case PHASE.FOURTH_EXECUTION:
        case PHASE.AIR_EXECUTION:
        case PHASE.PIPE_EXECUTION:
          this.execute();
          break;
        default:
          this.closeWizard();
          break;
      }
    },

    /** キャンセルボタン押下時の処理 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_161 = false;
    },
  },

  mounted() {
    this.init();
  },
};